export type LngLat = [number, number];

type PolygonGeometry = {
  type: 'Polygon';
  coordinates: number[][][];
};

type FeatureGeometry = {
  type: 'Feature';
  geometry: PolygonGeometry | null;
};

function isPolygonGeometry(value: unknown): value is PolygonGeometry {
  return Boolean(
    value &&
    typeof value === 'object' &&
    (value as PolygonGeometry).type === 'Polygon' &&
    Array.isArray((value as PolygonGeometry).coordinates),
  );
}

export function parseAreaGeoJson(raw?: string | null): LngLat[] {
  if (!raw?.trim()) {
    return [];
  }

  const parsed = JSON.parse(raw) as PolygonGeometry | FeatureGeometry;
  const geometry = isPolygonGeometry(parsed)
    ? parsed
    : parsed && typeof parsed === 'object' && (parsed as FeatureGeometry).type === 'Feature'
      ? (parsed as FeatureGeometry).geometry
      : null;

  if (!geometry || !isPolygonGeometry(geometry)) {
    throw new Error('Only GeoJSON Polygon shapes are supported');
  }

  const ring = geometry.coordinates?.[0] || [];
  if (ring.length === 0) {
    return [];
  }

  const normalized = ring.map((coordinate) => [coordinate[0], coordinate[1]] as LngLat);
  const first = normalized[0];
  const last = normalized[normalized.length - 1];

  if (first && last && first[0] === last[0] && first[1] === last[1]) {
    normalized.pop();
  }

  return normalized;
}

export function serializeAreaGeoJson(points: LngLat[]): string {
  if (points.length < 3) {
    return '';
  }

  const closedRing = [...points, points[0]].map(([lng, lat]) => [lng, lat]);
  return JSON.stringify(
    {
      type: 'Polygon',
      coordinates: [closedRing],
    },
    null,
    2,
  );
}

export function createEllipsePoints(center: LngLat, edge: LngLat, segments = 48): LngLat[] {
  const radiusLng = Math.abs(edge[0] - center[0]);
  const radiusLat = Math.abs(edge[1] - center[1]);

  if (radiusLng === 0 || radiusLat === 0) {
    return [];
  }

  return Array.from({ length: segments }, (_, index) => {
    const angle = (Math.PI * 2 * index) / segments;
    return [
      center[0] + Math.cos(angle) * radiusLng,
      center[1] + Math.sin(angle) * radiusLat,
    ] as LngLat;
  });
}

export function getLeafletPositions(points: LngLat[]): [number, number][] {
  return points.map(([lng, lat]) => [lat, lng]);
}

export function getAreaCenter(points: LngLat[]): [number, number] {
  if (points.length === 0) {
    return [43.2, -84.5];
  }

  const totals = points.reduce(
    (accumulator, [lng, lat]) => {
      accumulator.lat += lat;
      accumulator.lng += lng;
      return accumulator;
    },
    { lat: 0, lng: 0 },
  );

  return [totals.lat / points.length, totals.lng / points.length];
}